/**
 * Página Analizar - Analizador de Licitaciones
 *
 * Permite subir PDFs de licitaciones y analizarlos con IA
 * Muestra los resultados en una tabla y permite descargarlos en Excel
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import FileUpload from '../components/FileUpload';
import { analyzeLicitacion, checkJobStatus, confirmAndSave, downloadExcel, downloadPdf, downloadFile } from '../services/api';
import { COLORS } from '../theme/theme';

const Analizar = () => {
  const [files, setFiles] = useState([]);
  const [categoria] = useState('Energía'); // Estado para la categoría
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentField, setCurrentField] = useState('');
  const [results, setResults] = useState(null);
  const [pdfFileName, setPdfFileName] = useState(null); // Estado para el nombre del archivo PDF
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editedValues, setEditedValues] = useState({}); // Estado para valores editados manualmente
  const [editingFields, setEditingFields] = useState({}); // Estado para controlar qué campos están siendo editados
  const [comentarios, setComentarios] = useState(''); // Instrucciones adicionales del usuario
  const [allData, setAllData] = useState(null); // Datos de todos los lotes
  const [currentLoteIndex, setCurrentLoteIndex] = useState(0); // Índice del lote actual
  const [, setDataSaved] = useState(false); // Si los datos ya se guardaron en Excel

  // Manejar cambio de archivos
  const handleFilesChange = (newFiles) => {
    setFiles(newFiles);
    setError('');
    setSuccess('');
  };

  // Iniciar análisis
  const handleAnalyze = async () => {
    if (files.length === 0) {
      setError('Por favor, selecciona al menos un archivo PDF');
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);
    setError('');
    setSuccess('');
    setResults(null);
    setCurrentField('Iniciando análisis...');

    try {
      // Paso 1: Enviar archivos con categoría y obtener job_id (respuesta inmediata)
      const response = await analyzeLicitacion(files, categoria, comentarios);
      console.log('📥 Respuesta inicial del backend:', response);

      const job_id = response.job_id;

      if (!job_id) {
        throw new Error('No se recibió un ID de trabajo del servidor');
      }

      console.log(`✅ Análisis iniciado con job_id: ${job_id}`);
      setCurrentField('Análisis en cola...');

      // Paso 2: Hacer polling cada 3 segundos para consultar el estado
      const pollingInterval = setInterval(async () => {
        try {
          const status = await checkJobStatus(job_id);

          // Actualizar progreso y mensaje
          setProgress(status.progress || 0);
          setCurrentField(status.message || 'Procesando...');

          console.log(`[${status.progress}%] ${status.message} - Estado: ${status.status}`);

          // Verificar si el análisis terminó
          if (status.status === 'completed') {
            clearInterval(pollingInterval);
            setProgress(100);
            setCurrentField('¡Análisis completado!');

            // Verificar si hay datos en el resultado
            if (status.result && status.result.data) {
              // Guardar todos los datos (para múltiples lotes)
              const allLotesData = status.result.all_data || [status.result.data];
              setAllData(allLotesData);
              setCurrentLoteIndex(0);
              setResults(allLotesData[0]); // Mostrar primer lote
              setDataSaved(false); // Datos pendientes de guardar en Excel

              // Extraer el nombre del archivo PDF si existe
              if (status.result.pdf_file) {
                const pdfFilePath = status.result.pdf_file;
                // Extraer solo el nombre del archivo (última parte de la ruta)
                const fileName = pdfFilePath.split('/').pop().split('\\').pop();
                setPdfFileName(fileName);
                console.log('📄 PDF disponible:', fileName);
              }

              setSuccess('¡Análisis completado! Revisa los datos y pulsa "Guardar y Descargar Excel" para confirmar.');
              console.log('✅ Resultados recibidos:', status.result);
            } else {
              setError('El análisis se completó pero no se recibieron datos');
              console.error('❌ Respuesta sin datos:', status);
            }

            setIsAnalyzing(false);
          } else if (status.status === 'error') {
            clearInterval(pollingInterval);
            setError(status.error || status.message || 'Error al procesar el análisis');
            setIsAnalyzing(false);
          }
        } catch (pollError) {
          console.error('Error en polling:', pollError);
          clearInterval(pollingInterval);
          setError('Error al consultar el estado del análisis');
          setIsAnalyzing(false);
        }
      }, 3000); // Polling cada 3 segundos

      // Timeout de seguridad: cancelar después de 20 minutos
      setTimeout(() => {
        clearInterval(pollingInterval);
        if (isAnalyzing) {
          setError('El análisis está tardando demasiado. Por favor, inténtalo nuevamente.');
          setIsAnalyzing(false);
        }
      }, 1200000); // 20 minutos

    } catch (err) {
      console.error('Error al iniciar análisis:', err);
      setError(err.message || 'Error al analizar la licitación');
      setIsAnalyzing(false);
    }
  };

  // Guardar datos en Excel y descargar
  const handleDownloadExcel = async () => {
    try {
      // Primero guardar los datos (incluidas ediciones manuales) en el Excel del backend
      if (allData && allData.length > 0) {
        setSuccess('Guardando datos en Excel...');
        const saveResult = await confirmAndSave(allData, categoria);
        console.log('💾 Datos guardados:', saveResult);

        if (!saveResult.success) {
          setError(saveResult.message || 'Error al guardar los datos en Excel');
          return;
        }
        setDataSaved(true);
      }

      // Luego descargar el Excel
      const blob = await downloadExcel();
      downloadFile(blob, 'licitator.xlsx');
      setSuccess('Datos guardados y Excel descargado correctamente');
    } catch (err) {
      setError(err.message || 'Error al guardar/descargar el Excel');
    }
  };

  // Descargar PDF
  const handleDownloadPdf = async () => {
    if (!pdfFileName) {
      setError('No hay archivo PDF disponible para descargar');
      return;
    }

    try {
      const blob = await downloadPdf(pdfFileName);
      downloadFile(blob, pdfFileName);
      setSuccess('PDF descargado correctamente');
    } catch (err) {
      setError(err.message || 'Error al descargar el PDF');
    }
  };

  // Cambiar el lote que se muestra en la tabla
  const handleChangeLote = (event, newIndex) => {
    if (allData && allData[newIndex]) {
      setCurrentLoteIndex(newIndex);
      setResults(allData[newIndex]);
      setEditedValues({}); // Limpiar ediciones al cambiar de lote
      setEditingFields({});
    }
  };

  // Convertir objeto de resultados a array para la tabla
  const getResultsArray = () => {
    if (!results) return [];
    return Object.entries(results).map(([campo, valor]) => ({
      campo,
      valor: valor || '-',
    }));
  };

  // Detectar si un valor necesita edición manual
  const necesitaEdicionManual = (campo, valor) => {
    // Campos que son fechas y requieren edición si están vacíos
    const camposFecha = [
      'fecha',
      'plazo',
      'presentación',
      'presentacion',
      'límite',
      'limite',
      'apertura',
      'vencimiento',
    ];

    const esCampoFecha = camposFecha.some(keyword =>
      campo.toLowerCase().includes(keyword)
    );

    // Si contiene "INTRODUCIR MANUALMENTE" -> requiere edición
    if (valor && typeof valor === 'string' && valor.includes('INTRODUCIR MANUALMENTE')) {
      return true;
    }

    // Si es un campo de fecha y está vacío o tiene valor por defecto -> requiere edición
    if (esCampoFecha) {
      const valorVacio = !valor || valor === '-' || valor === 'N/A' || valor === 'No disponible' || valor.trim() === '';
      return valorVacio;
    }

    return false;
  };

  // Extraer el mensaje de aviso del valor
  const extraerMensajeAviso = (campo, valor) => {
    // Si contiene el mensaje del backend, mostrarlo
    if (valor && typeof valor === 'string' && valor.includes('INTRODUCIR MANUALMENTE')) {
      return valor;
    }
    // Si está vacío, mostrar mensaje genérico
    return `No se detectó ${campo} - Introducir manualmente`;
  };

  // Manejar cambio en el input de edición manual
  const handleManualInputChange = (campo, nuevoValor) => {
    setEditingFields(prev => ({
      ...prev,
      [campo]: nuevoValor
    }));
  };

  // Guardar el valor editado manualmente
  const handleGuardarValorManual = (campo) => {
    const nuevoValor = editingFields[campo];
    if (nuevoValor && nuevoValor.trim()) {
      // Actualizar el estado de resultados con el nuevo valor
      setResults(prev => ({
        ...prev,
        [campo]: nuevoValor.trim()
      }));
      // Guardar en editedValues para referencia
      setEditedValues(prev => ({
        ...prev,
        [campo]: nuevoValor.trim()
      }));
      // Limpiar el campo de edición
      setEditingFields(prev => {
        const newState = { ...prev };
        delete newState[campo];
        return newState;
      });
      setSuccess(`Campo "${campo}" actualizado correctamente`);
    }
  };

  // Obtener el valor actual para mostrar (editado o original)
  const getValorMostrar = (campo, valorOriginal) => {
    if (editedValues[campo]) {
      return editedValues[campo];
    }
    return valorOriginal;
  };

  return (
    <Container maxWidth="lg">
      {/* Encabezado */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Analizador de Licitaciones por IA
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          <strong>Licitator</strong> utiliza la <strong>API de OpenAI</strong> y técnicas de{' '}
          <strong>Búsqueda Semántica (RAG)</strong> para analizar automáticamente documentos de
          licitación en PDF y extraer la información más relevante de forma estructurada.
        </Typography>
      </Box>

      {/* Paso 1: Subida de archivos */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Paso 1: Subir Documentos PDF
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Selecciona los archivos PDF de las licitaciones (debe haber al menos un PDF)
        </Typography>

        <FileUpload
          onFilesChange={handleFilesChange}
          acceptedTypes=".pdf"
          multiple={true}
          label="Arrastra archivos PDF aquí o haz clic para seleccionar"
        />
      </Paper>

      {/* Paso 2: Comentarios adicionales */}
      {files.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Paso 2: Instrucciones Adicionales (Opcional)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Si necesitas dar instrucciones específicas sobre la licitación, escríbelas aquí.
            Por ejemplo: indicar a qué lote vas, qué información priorizar, o cualquier detalle relevante.
          </Typography>

          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Ej: Solo necesito la información del Lote 2 - Consultoría técnica. / El presupuesto está en el Anexo III. / Priorizar los criterios de valoración..."
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: COLORS.backgroundLight,
              },
            }}
          />
        </Paper>
      )}

      {/* Botón Analizar */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            fullWidth
          >
            {isAnalyzing ? 'Analizando...' : 'Analizar Licitación'}
          </Button>
        </Box>
      )}

      {/* Mensajes de error/éxito */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Paso 3: Progreso del análisis */}
      {isAnalyzing && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Progreso del Análisis
          </Typography>

          {/* Mensaje dinámico del backend */}
          <Box sx={{
            mb: 3,
            p: 2,
            backgroundColor: COLORS.backgroundLight,
            borderRadius: 2,
            borderLeft: `4px solid ${COLORS.teal}`
          }}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: COLORS.teal, mb: 0.5 }}>
              {currentField || 'Iniciando análisis...'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              El análisis puede tardar varios minutos dependiendo del tamaño del documento
            </Typography>
          </Box>

          {/* Barra de progreso */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progreso general
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.teal }}>
                {progress}% completado
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: COLORS.backgroundLight,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: COLORS.teal,
                  borderRadius: 6,
                },
              }}
            />
          </Box>

          {/* Información adicional */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            El sistema está extrayendo y analizando los 17 campos de información
          </Typography>
        </Paper>
      )}

      {/* Paso 4: Resultados */}
      {results && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Resultados del Análisis
            </Typography>
            <Chip
              icon={<CheckCircleIcon />}
              label="Análisis Completado"
              color="success"
              size="medium"
            />
          </Box>

          {/* Pestañas de lotes (solo si hay más de 1) */}
          {allData && allData.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Esta licitación tiene <strong>{allData.length} categorías</strong>. Selecciona una pestaña para ver los datos de cada categoría.
                Al guardar, se guardarán TODAS las categorías en filas separadas del Excel.
              </Alert>
              <Tabs
                value={currentLoteIndex}
                onChange={handleChangeLote}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    textTransform: 'none',
                  },
                }}
              >
                {allData.map((loteData, idx) => {
                  const entidades = loteData['Entidades Participantes'] || `Categoría ${idx + 1}`;
                  const tabLabel = entidades.length > 40
                    ? entidades.substring(0, 40) + '...'
                    : entidades;
                  return <Tab key={idx} label={tabLabel} />;
                })}
              </Tabs>
            </Box>
          )}

          {/* Tabla de resultados */}
          <TableContainer sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Campo
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Valor
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getResultsArray().map((row, index) => {
                  const valorMostrar = getValorMostrar(row.campo, row.valor);
                  const requiereEdicion = necesitaEdicionManual(row.campo, valorMostrar);

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        ...(requiereEdicion && {
                          backgroundColor: '#fff8e1 !important',
                          borderLeft: '4px solid #ff9800',
                        }),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, width: '30%', verticalAlign: 'top' }}>
                        {row.campo}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {requiereEdicion ? (
                          <Box>
                            {/* Mensaje de aviso */}
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              color: '#e65100',
                              backgroundColor: '#fff3e0',
                              p: 1.5,
                              borderRadius: 1,
                            }}>
                              <WarningIcon sx={{ mr: 1, fontSize: 20 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {extraerMensajeAviso(row.campo, valorMostrar)}
                              </Typography>
                            </Box>

                            {/* Input para introducir fecha manualmente */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                placeholder="DD/MM/AAAA HH:MM"
                                value={editingFields[row.campo] || ''}
                                onChange={(e) => handleManualInputChange(row.campo, e.target.value)}
                                sx={{
                                  flex: 1,
                                  maxWidth: 250,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    color: '#333333',
                                  },
                                  '& .MuiOutlinedInput-input::placeholder': {
                                    color: '#999999',
                                    opacity: 1,
                                  },
                                }}
                                InputProps={{
                                  sx: {
                                    fontSize: '0.9rem',
                                    color: '#333333',
                                  }
                                }}
                              />
                              <Tooltip title="Guardar fecha">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleGuardarValorManual(row.campo)}
                                  disabled={!editingFields[row.campo]?.trim()}
                                  sx={{
                                    backgroundColor: COLORS.primary,
                                    color: 'white',
                                    '&:hover': {
                                      backgroundColor: COLORS.primaryDark || '#1565c0',
                                    },
                                    '&:disabled': {
                                      backgroundColor: '#e0e0e0',
                                      color: '#9e9e9e',
                                    }
                                  }}
                                >
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ) : (
                          valorMostrar
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Botones de descarga */}
          <Typography variant="h6" gutterBottom>
            Descargar Resultados
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcel}
              sx={{
                flex: 1,
                backgroundColor: '#217346', // Verde Excel
                '&:hover': {
                  backgroundColor: '#1a5c38',
                },
              }}
            >
              Guardar y Descargar Excel
            </Button>

            {pdfFileName && (
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPdf}
                sx={{
                  flex: 1,
                  backgroundColor: '#d32f2f', // Rojo PDF
                  '&:hover': {
                    backgroundColor: '#9a0007',
                  },
                }}
              >
                Descargar PDF Resumen
              </Button>
            )}
          </Box>

          <Alert severity="info">
            Revisa los datos extraídos y completa los campos marcados en amarillo. Al pulsar "Guardar y Descargar Excel" se guardarán todos los datos (incluidas tus ediciones) en el Excel.
          </Alert>
        </Paper>
      )}

      {/* Información adicional */}
      {!results && !isAnalyzing && (
        <Paper sx={{ p: 3, backgroundColor: COLORS.backgroundLight }}>
          <Typography variant="h6" gutterBottom>
            Información del Analizador
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Solo tienes que subir tus <strong>PDFs de licitaciones públicas</strong> y obtendrás:
          </Typography>
          <ul style={{ marginLeft: 20 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Plazos Clave:</strong> Fechas límite, aperturas y periodos de
                presentación
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Requisitos de Solvencia:</strong> Económicos, técnicos y profesionales
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Criterios de Valoración:</strong> Ponderaciones, fórmulas y condiciones
                de adjudicación
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Y mucho más:</strong> Todo listo para análisis y seguimiento
              </Typography>
            </li>
          </ul>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            MET Licitator transforma documentos complejos en información útil, lista para la toma de
            decisiones.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default Analizar;
